/**
 * <s-video-carousel> — Scroll-snap carousel of video cards.
 *
 * Commands (from buttons via commandfor/command):
 *   --prev / --next  scroll one slide (wraps at the ends)
 *
 * Required markup: [data-carousel-track] containing one element per slide.
 * Listens for s-sound-video:unmute from child cards and mutes the others so
 * only one video plays with sound at a time.
 */
class SVideoCarousel extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('command', this.#handleCommand, { signal });
    this.addEventListener('s-sound-video:unmute', this.#handleUnmute, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #track() {
    return this.querySelector('[data-carousel-track]');
  }

  #handleUnmute = (event) => {
    for (const card of this.querySelectorAll('s-sound-video')) {
      if (card !== event.target && typeof card.mute === 'function') card.mute();
    }
  };

  #handleCommand = (event) => {
    if (event.command !== '--prev' && event.command !== '--next') return;
    const track = this.#track();
    const first = track?.firstElementChild;
    if (!track || !first) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = first.getBoundingClientRect().width + gap;
    const max = track.scrollWidth - track.clientWidth;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';
    if (event.command === '--next') {
      if (track.scrollLeft >= max - 2) track.scrollTo({ left: 0, behavior });
      else track.scrollBy({ left: step, behavior });
    } else if (track.scrollLeft <= 2) {
      track.scrollTo({ left: max, behavior });
    } else {
      track.scrollBy({ left: -step, behavior });
    }
  };
}

if (!customElements.get('s-video-carousel')) customElements.define('s-video-carousel', SVideoCarousel);
